import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, Square, Trash2 } from 'lucide-react';
import { Button, Textarea, toast } from '../../components/ui';
import { formatHours } from '../../lib/format';
import {
  useDiscardTimer,
  useAgreements,
  useStartTimer,
  useStopTimer,
  useTimer,
  useUpdateEntry,
  type TimeAgreement,
  type TimeTaskOption,
} from './api';
import { formatElapsed, todayIso } from './hours';
import { AgreementTaskPicker } from './AgreementTaskPicker';

/**
 * Topbar timer, visible on every screen (Harvest parity):
 *   • idle    → "Start timer" button opening a client → agreement → task cascade
 *   • running → pulsing dot + live elapsed + agreement, click for stop / discard
 * Elapsed is derived from the entry's `runningSince` so it survives reloads.
 *
 * ⚠ **THIS CLOCK IS ALWAYS YOURS.**
 *
 * It used to follow /time's person switcher, so a manager reviewing Andre's
 * week saw a topbar button reading "Start timer for Andre" — one click, on
 * every route in the app, from starting a live clock on a colleague's sheet
 * under a heading that looked exactly like their own. A timer measures the
 * person at the keyboard; there is no honest reading in which it measures
 * somebody else. It now sends no `personId` at all, so the SERVER resolves it
 * to the signed-in person's own sheet and the widget cannot be pointed
 * anywhere. Stopping a colleague's runaway clock is still possible where it
 * belongs — on their row in the week grid, where the sheet is named.
 */
export function TimerWidget() {
  const timerQuery = useTimer(null);
  const agreements = useAgreements();
  const startTimer = useStartTimer();
  const stopTimer = useStopTimer();
  const discardTimer = useDiscardTimer();
  const updateEntry = useUpdateEntry();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const [note, setNote] = useState('');
  const noteEntryId = useRef<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const startRef = useRef<HTMLButtonElement>(null);

  const timer = timerQuery.data?.timer ?? null;

  // Adopt the running entry's note once per timer — never mid-typing.
  useEffect(() => {
    if (!timer) {
      noteEntryId.current = null;
      return;
    }
    if (noteEntryId.current !== timer.entryId) {
      noteEntryId.current = timer.entryId;
      setNote(timer.notes ?? '');
    }
  }, [timer]);

  /**
   * Persist the description onto the running entry.
   *
   * Timer-created entries were systematically note-less — exactly the entries an
   * invoice line needs a description for. Writing it WHILE the clock runs is the
   * only moment the work is fresh, so the note lives in the timer popover and is
   * flushed again on stop.
   */
  const saveNote = async () => {
    if (!timer) return;
    const next = note.trim();
    if (next === (timer.notes ?? '')) return;
    try {
      await updateEntry.mutateAsync({ id: timer.entryId, notes: next || null });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Saving the note failed.');
    }
  };

  // Tick every second while running.
  useEffect(() => {
    if (!timer) return;
    const interval = window.setInterval(() => setNow(Date.now()), 1000);
    setNow(Date.now());
    return () => window.clearInterval(interval);
  }, [timer]);

  // Close the running-timer popover on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onClick = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Starting a timer closes the popovers; the running pill takes over.
  const start = (agreement: TimeAgreement, task: TimeTaskOption | null) => {
    setPickerOpen(false);
    startTimer.mutate(
      {
        agreementId: agreement.id,
        taskId: task?.id ?? null,
        date: todayIso(),
      },
      {
        onSuccess: () =>
          toast.success(`Timer running on ${agreement.name}${task ? ` · ${task.name}` : ''}.`),
        onError: (error) =>
          toast.error(error instanceof Error ? error.message : 'Starting the timer failed.'),
      },
    );
  };

  const stop = async () => {
    // Flush whatever is in the note box before the entry stops being "running".
    await saveNote();
    const described = Boolean(note.trim() || timer?.notes);
    stopTimer.mutate(
      {},
      {
        onSuccess: (result) => {
          setOpen(false);
          toast.success(
            described
              ? `Timer stopped — ${formatHours(result.elapsedHours, { unit: true })} added.`
              : `Timer stopped — ${formatHours(result.elapsedHours, {
                  unit: true,
                })} added with no description. Add one on the timesheet so it can be invoiced.`,
          );
        },
        onError: (error) =>
          toast.error(error instanceof Error ? error.message : 'Stopping the timer failed.'),
      },
    );
  };

  const discard = () => {
    discardTimer.mutate(
      {},
      {
        onSuccess: () => {
          setOpen(false);
          toast.info('Timer discarded — no time was saved.');
        },
        onError: (error) =>
          toast.error(error instanceof Error ? error.message : 'Discarding the timer failed.'),
      },
    );
  };

  // --- idle: start affordance ----------------------------------------------
  if (!timer) {
    const list = agreements.data?.agreements ?? [];
    return (
      <div className="relative">
        <button
          ref={startRef}
          type="button"
          onClick={() => setPickerOpen((o) => !o)}
          aria-expanded={pickerOpen}
          disabled={startTimer.isPending || list.length === 0}
          title="Start a timer on your own timesheet"

          className="flex h-9 items-center gap-1.5 rounded-full border border-grey-200 bg-white pl-2.5 pr-3.5 text-sm font-semibold text-secondary transition-colors hover:border-primary/40 hover:bg-primary/5 disabled:opacity-50"
        >
          <Play className="h-3.5 w-3.5 fill-current text-primary" aria-hidden />
          <span className="hidden sm:inline">Start timer</span>
          <span className="sr-only sm:hidden">Start timer</span>
        </button>
        {pickerOpen && (
          <AgreementTaskPicker
            agreements={list}
            personId={null}
            placement="below-right"
            onPick={start}
            onClose={() => setPickerOpen(false)}
            triggerRef={startRef}
          />
        )}
      </div>
    );
  }

  // --- running --------------------------------------------------------------
  const elapsedMs = now - new Date(timer.startedAt).getTime();
  const busy = stopTimer.isPending || discardTimer.isPending;

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-label={`Timer running on ${timer.agreement.name}`}
        className="flex h-9 items-center gap-2 rounded-full border border-primary/30 bg-primary/5 pl-3 pr-3.5 transition-colors hover:bg-primary/10"
      >
        <span className="relative flex h-2 w-2" aria-hidden>
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-60" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
        </span>
        <span className="text-sm font-bold text-primary num">{formatElapsed(elapsedMs)}</span>
        <span className="hidden max-w-[10rem] truncate text-sm font-medium text-secondary sm:block">
          {timer.agreement.name}
        </span>
      </button>

      {open && (
        <div className="absolute right-0 top-full z-40 mt-2 w-72 rounded-xl border border-grey-200 bg-white p-4 shadow-elevated">
          <p className="truncate text-sm font-bold text-secondary">{timer.agreement.name}</p>
          <p className="truncate text-caption text-grey-500">
            {timer.task ? (
              <>
                <span className="font-semibold text-grey-600">{timer.task.name}</span>
                {' · '}
              </>
            ) : null}
            {timer.agreement.client.name}
          </p>
          <p className="mt-3 text-center text-3xl font-black text-secondary num">
            {formatElapsed(elapsedMs)}
          </p>
          {timer.baseHours > 0 && (
            <p className="mt-1 text-center text-caption text-grey-500">
              + {formatHours(timer.baseHours, { unit: true })} already logged today
            </p>
          )}
          <div className="mt-3">
            <Textarea
              label="What are you working on?"
              value={note}
              onChange={(event) => setNote(event.target.value)}
              onBlur={saveNote}
              rows={2}
              placeholder="Description for the invoice line…"
              textareaClassName="resize-none text-sm"
            />
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={discard}
              disabled={busy}
              className="text-grey-500"
            >
              <Trash2 className="h-4 w-4" aria-hidden /> Discard
            </Button>
            <Button size="sm" onClick={() => void stop()} loading={busy}>
              <Square className="h-3.5 w-3.5 fill-current" aria-hidden /> Stop
            </Button>
          </div>
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              navigate('/time');
            }}
            className="mt-3 w-full text-center text-caption font-semibold text-accent hover:underline"
          >
            Open timesheet
          </button>
        </div>
      )}
    </div>
  );
}
